import React, { useState, useEffect } from 'react';
import { Card, CardContent, Button, Table, TableHead, TableRow, TableCell, TableBody, Typography } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ReviewForm from './ReviewForm';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';

export default function ReviewPanel() {
  const [reviews, setReviews] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  
  const handleAdd = () => {
    setEditData(null);
    setDialogOpen(false);
    setTimeout(() => setDialogOpen(true), 0);
  };

  const handleSave = async (data) => {
    try {
      const isEdit = Boolean(data._id);
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/reviews${isEdit ? `/${data._id}` : ''}`,
        {
          method: isEdit ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        }
      );

      const saved = await res.json();
      if (!saved._id) {
        console.error('Save failed or missing _id:', saved);
        return;
      }

      setReviews(prev => {
        const index = prev.findIndex(r => r._id === saved._id);
        if (index !== -1) {
          const updated = [...prev];
          updated[index] = saved;
          return updated;
        }
        return [saved, ...prev];
      });

      setDialogOpen(false);
      setEditData(null);
    } catch (err) {
      console.error('Failed to save review:', err);
    }
  };

  const handleDelete = async (id) => {
    try {
      await fetch(`${process.env.REACT_APP_API_URL}/api/reviews/${id}`, {
        method: 'DELETE',
      });
      setReviews(prev => prev.filter(r => r._id !== id));
    } catch (err) {
      console.error('Failed to delete review:', err);
    }
  };


  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));
    // Try to extract propertyId from different possible user shapes
    const propertyId =
      user?.propertyId?.id ||
      user?.properties?.[0]?.propertyId?.id;

    if (!propertyId) return;

    fetch(`${process.env.REACT_APP_API_URL}/api/reviews?propertyId=${propertyId}`)
      .then(res => res.json())
      .then(data => setReviews(data))
      .catch(err => console.error('Failed to fetch reviews:', err));
  }, []);

  return (
    <div className="p-6 space-y-6">
      <Typography variant="h5" fontWeight={600}>Manage Reviews</Typography>
      <Card>
        <CardContent>
          <Button variant="contained" onClick={handleAdd}>Add Review</Button>
          <Table sx={{ mt: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell>Guest</TableCell>
                <TableCell>Rating</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Submitted By</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reviews.map((review) => (
                <TableRow
                  key={review._id}
                  onClick={() => { setSelectedReview(review); setViewDialogOpen(true); }}
                  hover
                  style={{ cursor: 'pointer' }}
                >
                  <TableCell>{review.guestName || 'Anonymous'}</TableCell>
                  <TableCell>{review.rating} ⭐</TableCell>
                  <TableCell>{review.title || '-'}</TableCell>
                  <TableCell>{review.date}</TableCell>
                  <TableCell>{review.source}</TableCell>
                  <TableCell>{review.submittedBy}</TableCell>
                  <TableCell>
                    <IconButton onClick={(e) => { e.stopPropagation(); setEditData(review); setDialogOpen(true); }}>
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={(e) => { e.stopPropagation(); handleDelete(review._id); }}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <ReviewForm
        key={dialogOpen ? editData?._id || 'new' : 'closed'}
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={handleSave}
        initialData={editData}
      />
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Review Details</DialogTitle>
        <DialogContent dividers>
          <Typography><strong>Guest:</strong> {selectedReview?.guestName || 'Anonymous'}</Typography>
          <Typography><strong>Rating:</strong> {selectedReview?.rating} ⭐</Typography>
          <Typography><strong>Title:</strong> {selectedReview?.title || '(No title)'}</Typography>
          <Typography><strong>Date:</strong> {selectedReview?.date}</Typography>
          <Typography><strong>Source:</strong> {selectedReview?.source}</Typography>
          <Typography><strong>Submitted By:</strong> {selectedReview?.submittedBy}</Typography>
          <Typography sx={{ mt: 2 }}><strong>Content:</strong></Typography>
          <Typography>{selectedReview?.content || '(No content)'}</Typography>
        </DialogContent>
      </Dialog>
    </div>
  );
}
